import {
  ASSIGNED_SEATING,
  ASSIGNED_SEATING_MODES,
  common,
} from "@shared/constants";
import { CURRENCY_MAP } from "@shared/constants/enums";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { useTranslation } from "@shared/utils/internationalization";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import SeatingService from "../../services/SeatingService";
import { SeatingItemPropertyMap } from "../../types/seating-property-types";
import { LegendContentProps } from "./LegendContent.props";

export default function LegendContent({
  basketType,
  advancedColors,
  itemTypes,
  allInPricing,
  mapItemType,
  currency = CURRENCY_MAP.CAD.id,
  hidePrice = false,
  hideMinPurchaseText = false,
}: LegendContentProps) {
  const { t } = useTranslation("seating");
  const { paymentType, purchaseSourcePlatform } = useBasketService();
  const isBoxOffice = basketType === common.DataProvider.Venue;
  const { seatingServiceState } = useSeatingState();
  const isAttendanceMap =
    seatingServiceState.mode === ASSIGNED_SEATING_MODES.ATTENDANCE.id;
  // Returns the seat_permission_color property on the map item
  // Or the default colors from the ASSIGNED_SEATING.TICKET_TYPE_COLORS object
  const getColorFill = (item: SeatingItemPropertyMap, idx: number) => {
    const seatingColors = Object.values(ASSIGNED_SEATING.TICKET_TYPE_COLORS);

    return (
      item.seat_permission_color ||
      seatingColors[idx % seatingColors.length].colorFill
    );
  };

  const styles = ASSIGNED_SEATING.SEAT.STYLE;

  const alwaysDisplayedOnLegend = (
    <Flex direction="column" gap="md">
      {!isAttendanceMap && (
        <Flex alignItems="center">
          <Flex
            width="element.md"
            height="element.md"
            borderRadius="50%"
            marginRight="xs"
            alignItems="center"
            justifyContent="center"
            style={{
              background: styles.CHOSEN.colorFill,
            }}
          >
            <Icon.Checkmark.Solid
              boxSize="element.sm"
              color={styles.CHOSEN.colorStroke}
            />
          </Flex>
          <Text color="font.dark">{t(styles.CHOSEN.name)}</Text>
        </Flex>
      )}
      {isAttendanceMap && (
        <Flex alignItems="center">
          <Box minW="element.xs" mr="md">
            {styles.HOLD.seatLabel}
          </Box>
          <Text color="font.dark">{t(styles.HOLD.name)}</Text>
        </Flex>
      )}
      <Flex alignItems="center">
        <Box minW="element.xs" mr="md">
          {styles.RESALE.seatLabel}
        </Box>
        <Text color="font.dark">{t(styles.RESALE.name)}</Text>
      </Flex>
      <Flex alignItems="center">
        <Box minW="element.xs" mr="md">
          {styles.ACCESSIBLE.seatLabel}
        </Box>
        <Text color="font.dark">{t(styles.ACCESSIBLE.name)}</Text>
      </Flex>
      <Flex alignItems="center">
        <Box minW="element.xs" mr="md">
          {styles.LIMITED_VISIBILITY.seatLabel}
        </Box>
        <Text color="font.dark">{t(styles.LIMITED_VISIBILITY.name)}</Text>
      </Flex>
      <Flex alignItems="center">
        <Text
          width="element.md"
          height="element.md"
          borderRadius="50%"
          marginRight="xs"
          style={{ background: styles.UNAVAILABLE.colorFill }}
        />
        <Text color="font.dark">{t(styles.UNAVAILABLE.name)}</Text>
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" gap="md">
      {advancedColors ? (
        // Advanced Seat Colors
        itemTypes
          .sort((a, b) => SeatingService.sortPermissions(a, b, mapItemType))
          .map((item, idx) => (
            <Flex key={item.id} alignItems="center">
              <Text
                width="element.md"
                height="element.md"
                borderRadius="50%"
                marginRight="xs"
                style={{ background: getColorFill(item, idx) }}
              />
              <Flex direction="column">
                <Text fontWeight="bold" color="font.dark">
                  {item.name}
                </Text>
                {!hidePrice && (
                  <Text fontSize="sm" color="font.dark">
                    <CurrencyFormat
                      value={getItemPrice(
                        item?.price?.toString(),
                        allInPricing,
                        item?.fees_pricing_info,
                        paymentType,
                        purchaseSourcePlatform
                      )}
                      displayType="text"
                      currency={currency}
                      includeSuffix
                    />
                    <AllInPricing
                      hasAllInPricing={allInPricing}
                      feesPricingInfo={item?.fees_pricing_info}
                      price={item?.price?.toString()}
                      currency={currency}
                      purchaseSourcePlatform={purchaseSourcePlatform}
                      paymentType={paymentType}
                    />
                  </Text>
                )}
                {!hideMinPurchaseText &&
                  item.minimum_purchase_limit &&
                  item.minimum_purchase_limit > 1 && (
                    <Text fontSize="sm" color="secondary.400">
                      {/* TODO: fix desktop translations */}
                      {isBoxOffice
                        ? `Minimum ${item.minimum_purchase_limit} tickets required`
                        : t("Minimum {{count}} tickets required", {
                            count: item.minimum_purchase_limit,
                          })}
                    </Text>
                  )}
              </Flex>
            </Flex>
          ))
      ) : (
        // Basic Seat Colors
        <Flex alignItems="center">
          <Text
            width="element.md"
            height="element.md"
            borderRadius="50%"
            marginRight="xs"
            style={{ background: styles.AVAILABLE.colorFill }}
          />
          <Text color="font.dark">
            {styles.AVAILABLE[isAttendanceMap ? "attendanceModeName" : "name"]}
          </Text>
        </Flex>
      )}
      {alwaysDisplayedOnLegend}
    </Flex>
  );
}
